import { ChangeEvent, useEffect, useRef, useState } from "react"
import Cell from "./Cell"
import TextField from "./TextField"
import { Icon } from "../shared/Icon"
import Button from "@/components/shared/Button"
import Tooltip from "@/components/shared/Tooltip"
import { fieldConfig } from "@/pages/ControlPanel/config"
import { ButtonVariant, ColorVariant } from "@/types/index"
import { getErrorMessage } from "@/utils/index"

const HOVER_DELAY_MS = 500

interface EntraIdProps {
   entraTid: string
   consentUrl?: string
   entraSsoActive?: boolean
   errorFacility?: number | null
   isEditMode: boolean
   onEntraTidChange: (e: ChangeEvent<HTMLInputElement>) => void
   onActivate: () => void
   onDeactivate: () => void
   onHover: () => void
}

const EntraId = ({
   entraTid,
   consentUrl,
   entraSsoActive,
   errorFacility,
   isEditMode,
   onEntraTidChange,
   onActivate,
   onDeactivate,
   onHover,
}: EntraIdProps) => {
   const [hasConsentUrl, setHasConsentUrl] = useState(false)
   const [tooltipContent, setTooltipContent] = useState({ index: 0 })
   const [showCopied, setShowCopied] = useState(false)
   const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
      undefined
   )

   useEffect(() => () => clearTimeout(hoverTimeoutRef.current), [])

   if (entraSsoActive) {
      return (
         <div className="customers-facilities-table__entra-id-wrapper">
            <Icon
               name="microsoft"
               size="16px"
            />
            <span>Active</span>
            {isEditMode && (
               <Button
                  className="customers-facilities-table__entra-id-button"
                  variant={ButtonVariant.SMALL}
                  color={ColorVariant.CERISE}
                  onClick={onDeactivate}
               >
                  Deactivate
               </Button>
            )}
         </div>
      )
   }

   if (isEditMode) {
      const isEntraTidValid = fieldConfig.entraTid?.validate(entraTid || "")

      return (
         <span className="customers-facilities-table__entra-id-wrapper">
            <Cell
               name="entraTid"
               value={entraTid || ""}
               isEditMode
               error={!isEntraTidValid}
               handleCellChange={onEntraTidChange}
               placeholder={fieldConfig.entraTid?.placeholder}
            >
               <TextField name="entraTid">{entraTid || "-"}</TextField>
            </Cell>
         </span>
      )
   }

   const handleHover = () => {
      if (entraTid && !consentUrl && !hasConsentUrl) {
         hoverTimeoutRef.current = setTimeout(() => {
            setHasConsentUrl(true)
            onHover()
         }, HOVER_DELAY_MS)
      }
   }

   const handleHoverEnd = () => {
      clearTimeout(hoverTimeoutRef.current)
   }

   const copyToClipboard = () => {
      if (!consentUrl) {
         return
      }
      navigator.clipboard.writeText(consentUrl)
      setShowCopied(true)
      setTooltipContent((prev) => ({ index: prev.index + 1 }))
      setTimeout(() => {
         setShowCopied(false)
         setTooltipContent((prev) => ({ index: prev.index + 1 }))
      }, 1200)
   }

   const getTooltipText = () => {
      if (consentUrl) return showCopied ? "Copied!" : `Copy: ${consentUrl}`
      if (hasConsentUrl && errorFacility) return getErrorMessage(errorFacility)
      return "Generating link…"
   }

   const tooltipText = getTooltipText()

   return (
      <span className="customers-facilities-table__entra-id-wrapper">
         {entraTid ? (
            <>
               <Icon
                  name="microsoft"
                  size="16px"
               />
               <Tooltip
                  key={tooltipContent.index}
                  content={tooltipText}
                  wrap
               >
                  <span
                     className="customers-facilities-table__entra-id"
                     onClick={copyToClipboard}
                     onMouseEnter={handleHover}
                     onMouseLeave={handleHoverEnd}
                  >
                     {entraTid}
                  </span>
               </Tooltip>
            </>
         ) : (
            <>
               <Icon
                  name="microsoft"
                  size="16"
               />
               <span>-</span>
            </>
         )}
         {entraTid && (
            <Button
               className="customers-facilities-table__entra-id-button"
               variant={ButtonVariant.SMALL}
               onClick={onActivate}
            >
               Activate
            </Button>
         )}
      </span>
   )
}

export default EntraId
